import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CaretLeft, ShieldCheck, IdentificationCard, Camera, Certificate, Check, CircleNotch, Clock } from '@phosphor-icons/react';

export default function VettingPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ nin: '', id_document_type: 'national_id', professional_indemnity: false, years_experience: 1, certifications: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/vetting/status');
        setStatus(data.vetting);
      } catch {} finally { setLoading(false); }
    };
    load();
  }, [api]);

  const submit = async () => {
    if (form.nin.length !== 11) { alert('NIN must be 11 digits'); return; }
    setSubmitting(true);
    try {
      await api.post('/vetting/submit', form);
      const { data } = await api.get('/vetting/status');
      setStatus(data.vetting);
    } catch (err) { alert(err.response?.data?.detail || 'Submission failed'); } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] max-w-[480px] mx-auto pb-10" data-testid="vetting-page">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
        <h1 className="font-heading text-lg font-bold text-[#1C1C1C]">Verification</h1>
      </header>

      <div className="p-4">
        {/* Status Banner */}
        {status && (
          <div className={`rounded-xl p-4 mb-4 flex items-center gap-3 ${status.status === 'approved' ? 'bg-[#2D6A4F]/10 border border-[#2D6A4F]/20' : status.status === 'rejected' ? 'bg-red-50 border border-red-200' : 'bg-[#F4A261]/10 border border-[#F4A261]/20'}`} data-testid="vetting-status">
            {status.status === 'approved' ? <ShieldCheck size={24} className="text-[#2D6A4F]" /> : status.status === 'rejected' ? <ShieldCheck size={24} className="text-red-500" /> : <Clock size={24} className="text-[#F4A261]" />}
            <div>
              <p className="font-bold text-sm font-body">{status.status === 'approved' ? 'Verified' : status.status === 'rejected' ? 'Rejected' : 'Under Review'}</p>
              <p className="text-xs text-[#5C5C5C]">{status.status === 'approved' ? 'Your identity has been verified' : status.status === 'rejected' ? 'Please resubmit with correct documents' : 'Your documents are being reviewed'}</p>
            </div>
          </div>
        )}

        {(!status || status.status === 'rejected') && (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <ShieldCheck size={32} weight="duotone" className="text-[#1A4D2E]" />
              </div>
              <h2 className="font-heading text-xl font-bold text-[#1C1C1C] mb-1">Get Verified</h2>
              <p className="text-sm text-[#5C5C5C] font-body">Complete verification to start receiving jobs</p>
            </div>

            <div className="bg-white border border-[#D4C5B3] rounded-xl p-5 space-y-4">
              {/* NIN */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 flex items-center gap-2"><IdentificationCard size={14} /> NIN (11 digits)</label>
                <input data-testid="vetting-nin" type="tel" maxLength={11} value={form.nin} onChange={e => setForm(f => ({ ...f, nin: e.target.value.replace(/\D/g, '').slice(0, 11) }))}
                  placeholder="Enter your 11-digit NIN" className="w-full bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none focus:border-[#1A4D2E]" />
                <p className={`text-xs mt-1 ${form.nin.length === 11 ? 'text-[#2D6A4F]' : 'text-[#5C5C5C]'}`}>{form.nin.length}/11 digits</p>
              </div>

              {/* ID Type */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">ID Document Type</label>
                <select data-testid="vetting-id-type" value={form.id_document_type} onChange={e => setForm(f => ({ ...f, id_document_type: e.target.value }))}
                  className="w-full bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none">
                  <option value="national_id">National ID Card</option>
                  <option value="drivers_license">Driver's License</option>
                  <option value="voters_card">Voter's Card</option>
                  <option value="passport">International Passport</option>
                </select>
              </div>

              {/* Years Experience */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Years of Experience</label>
                <input data-testid="vetting-experience" type="number" min={0} max={50} value={form.years_experience}
                  onChange={e => setForm(f => ({ ...f, years_experience: parseInt(e.target.value) || 0 }))}
                  className="w-full bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none" />
              </div>

              {/* Certifications */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 flex items-center gap-2"><Certificate size={14} /> Certifications</label>
                <textarea data-testid="vetting-certs" value={form.certifications} onChange={e => setForm(f => ({ ...f, certifications: e.target.value }))}
                  placeholder="List any relevant certifications..." rows={3}
                  className="w-full bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none resize-none" />
              </div>

              {/* Professional Indemnity */}
              <div className="flex items-center gap-3 bg-[#F4A261]/5 border border-[#F4A261]/20 rounded-xl p-4">
                <input data-testid="vetting-indemnity" type="checkbox" checked={form.professional_indemnity}
                  onChange={e => setForm(f => ({ ...f, professional_indemnity: e.target.checked }))}
                  className="w-5 h-5 rounded accent-[#E86A33]" />
                <div>
                  <p className="text-sm font-bold text-[#1C1C1C] font-body">Professional Indemnity Cover</p>
                  <p className="text-xs text-[#5C5C5C]">Required for all artisans and facility managers</p>
                </div>
              </div>

              <button onClick={submit} disabled={submitting || form.nin.length !== 11}
                className="w-full bg-[#1A4D2E] hover:bg-[#133922] text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50 font-body" data-testid="submit-vetting">
                {submitting ? <><CircleNotch size={18} className="animate-spin" /> Submitting...</> : <><ShieldCheck size={18} /> Submit for Verification</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
